/**
 * Weirwood
 * Copyright 2019 Baidu Inc. All rights reserved.
 *
 * @file 用户行为监控插件
 * @author zengxiaohui(zengxiaohui@baidu.com)
 */

import {Plugin} from './Plugin';
import {fill} from '../utils/fill';
import {formatBehaviorData, parseUrl, transformResponse} from '../utils/misc';
import {wrap} from '../utils/wrapper';
import {isFunction, isString} from '../utils/is';
import {getGlobal} from '../utils/global';
import {supportsDocument, supportsFetch, supportsHistory} from '../utils/supports';
import {breadcrumbEventHandler, keypressEventHandler} from './helper';
import {Weirwood} from '../interface/options.interface';

const global = getGlobal() as Window;
let lastHref: string | undefined;

const MAX_RES_LENGTH = 8192;
const REDIRECT_CATCH_STATUS = [301, 302, 304];

/** 行为数据接口 */
export interface BehaviorPluginOptions {
    beacon: boolean;
    console: boolean;
    dom: true;
    fetch: true;
    history: true;
    weirwood: true;
    xhr: true;
    [k: string]: any;
}

/**
 * 行为数据插件
 */
export class BehaviorPlugin implements Plugin {
    static id = 'behaviors';
    name = BehaviorPlugin.id;
    client: any;
    private readonly options: BehaviorPluginOptions;

    constructor(options?: BehaviorPluginOptions) {
        this.options = {
            beacon: true,
            console: true,
            dom: true,
            fetch: true,
            history: true,
            weirwood: true,
            xhr: true,
            ...options
        };
    }

    /**
     * 安装插件
     * @param client
     */
    setupOnce(client: Weirwood) {
        this.client = client;
        const keys = Object.keys(this.options);
        for (const k of keys) {
            const funcName = 'instrument' + k[0].toUpperCase() + k.slice(1);
            if (this.options[k] && this[funcName] && typeof this[funcName] === 'function') {
                this[funcName].call(this);
            }
        }
    }

    /**
     * 监听 DOM 事件
     */
    instrumentDom() {
        if (!supportsDocument()) {
            return;
        }

        const d = global.document;
        d.addEventListener('click', breadcrumbEventHandler.call(this, this.client, 'click'), false);
        d.addEventListener('keypress', keypressEventHandler.call(this, this.client), false);
    }

    /**
     * 监听 XHR 网络请求事件
     */
    instrumentXhr() {
        const client = this.client;
        const {collectAutoAPIException, isApiException} = client.options;
        if (!('XMLHttpRequest' in global)) {
            return;
        }

        /** JSDoc */
        function wrapProp(prop: string, xhr: XMLHttpRequest): void {
            // TODO: Fix XHR types
            if (prop in xhr && isFunction((xhr as {[key: string]: any})[prop])) {
                fill(xhr, prop, (original: any) =>
                    wrap(
                        {
                            mechanism: {
                                data: {
                                    function: prop,
                                    handler: (original && original.name) || '<anonymous>'
                                },
                                handled: true,
                                type: 'instrument'
                            }
                        },
                        original
                    )
                );
            }
        }

        const xhrproto = XMLHttpRequest.prototype;
        const globalKey = client._globalKey || '';

        // hack xhr open 方法
        fill(
            xhrproto,
            'open',
            function(origOpen: any) {
                return function(method: string, url: any) {
                    // preserve arity
                    // if Sentry key appears in URL, don't capture
                    if (isString(url) && url.indexOf(globalKey) === -1) {
                        // 记录 __raven_xhr 数据
                        this.__raven_xhr = {
                            method: method,
                            url: url,
                            status_code: null,
                        };
                    }

                    return origOpen.apply(this, arguments);
                };
            },
            client._wrappedBuiltIns
        );

        fill(
            xhrproto,
            'abort',
            function(origAbort: any) {
                return function() {
                    const xhr = this;
                    if (xhr.__raven_xhr) {
                        xhr.__raven_xhr.abortedFlag = true;
                    }
                    return origAbort.apply(this, arguments);
                };
            },
            client._wrappedBuiltIns
        );

        // hack xhr send 方法
        fill(
            xhrproto,
            'send',
            function(origSend: any) {
                return function() {
                    const xhr = this;
                    const sendBody = arguments;
                    // 自定义的回调处理函数
                    function onreadystatechangeHandler() {
                        if (!xhr.__raven_xhr || xhr.readyState !== 4) {
                            return;
                        }
                        try {
                            // touching statusCode in some platforms throws
                            // an exception
                            xhr.__raven_xhr.status_code = xhr.status;
                        } catch (e) {
                            /* do nothing */
                        }
                        // 对response进行判断和处理生成isApiException需要的信息
                        const res = transformResponse(xhr.responseType, xhr.response);

                        // 发送前，捕获行为
                        client.captureBehavior({
                            type: 'http',
                            category: 'xhr',
                            data: {
                                ...xhr.__raven_xhr,
                                response: formatBehaviorData(res),
                                params: formatBehaviorData((sendBody && sendBody[0]) ? sendBody[0] : '')
                            }, // status_code, method, url
                            
                        });

                        // 是否自动上报
                        if (!collectAutoAPIException) {
                            return;
                        }

                        // abort 的请求不上报
                        if (xhr.__raven_xhr && xhr.__raven_xhr.abortedFlag) {
                            return;
                        }

                        // 是否需要上报
                        let needCapture = false;

                        const isSucc = xhr.status >= 200 && xhr.status <= 299;
                        const isRedirectAndCache = REDIRECT_CATCH_STATUS.indexOf(xhr.status) !== -1;


                        // 自动上报 API 异常，这里 readyState只处理4
                        // 状态码不为200~299且不为缓存状态码上报
                        if (!isSucc && !isRedirectAndCache) {
                            // 针对重定向和缓存，不做处理
                            needCapture = true;
                        }

                        // 状态码为200~299且isApiException用户自定义判断返回true上报
                        if (isSucc && isApiException && isFunction(isApiException)) {
                            const requestData = {
                                method: xhr.__raven_xhr.method,
                                url: xhr.responseURL,
                                status: xhr.status,
                                responseType: xhr.responseType,
                                response: res
                            };
                            needCapture = !!isApiException(requestData);
                        }
                        if (needCapture) {
                            let responseHeaders = '';
                            if (isFunction(xhr.getAllResponseHeaders)) {
                                responseHeaders = xhr.getAllResponseHeaders().trim().replace(/[\r\n]+/g, '***');
                            }
                            // 这里进行上报
                            client.captureAPIException({
                                url: xhr.responseURL || xhr.__raven_xhr.url,
                                method: xhr.__raven_xhr.method,
                                params: (sendBody && sendBody[0]) ? sendBody[0] : '',
                                status: xhr.status,
                                response: (isString(res) ? res : JSON.stringify(res)).slice(0, MAX_RES_LENGTH),
                                responseHeaders: responseHeaders,
                                user: '',
                                extra: 'AUTO_XHR' // 标识是自动上报的 API 异常
                            });
                        }
                    }

                    const props = ['onload', 'onerror', 'onprogress'];
                    for (let j = 0; j < props.length; j++) {
                        wrapProp(props[j], xhr);
                    }

                    // 如果定义了 xhr 处理函数，hack 该函数
                    if ('onreadystatechange' in xhr && isFunction(xhr.onreadystatechange)) {
                        fill(
                            xhr,
                            'onreadystatechange',
                            function(orig: any) {
                                return wrap(
                                    {
                                        mechanism: {
                                            type: 'instrument',
                                            data: {
                                                function: 'onreadystatechange',
                                                handler: (orig && orig.name) || '<anonymous>'
                                            }
                                        }
                                    },
                                    orig,
                                    onreadystatechangeHandler
                                );
                            } /* intentionally don't track this instrumentation */
                        );
                    } else {
                        // if onreadystatechange wasn't actually set by the page on this xhr, we
                        // are free to set our own and capture the breadcrumb
                        xhr.onreadystatechange = onreadystatechangeHandler;
                    }

                    return origSend.apply(this, arguments);
                };
            },
            client._wrappedBuiltIns
        );
    }

    /**
     * 监听 Fetch 网络请求事件
     */
    instrumentFetch() {
        if (!supportsFetch()) {
            return;
        }

        const _window: any = getGlobal();
        const client = this.client;
        const {collectAutoAPIException, isApiException} = client.options;
        const globalKey = client._globalKey;
        fill(
            global,
            'fetch',
            function(origFetch: any) {
                return function() {
                    // preserve arity
                    // Make a copy of the arguments to prevent deoptimization
                    // https://github.com/petkaantonov/bluebird/wiki/Optimization-killers#32-leaking-arguments
                    const args = new Array(arguments.length);
                    for (let i = 0; i < args.length; ++i) {
                        args[i] = arguments[i];
                    }

                    const fetchInput = args[0];
                    let method = 'GET';
                    let url;

                    if (typeof fetchInput === 'string') {
                        url = fetchInput;
                    } else if ('Request' in _window && fetchInput instanceof _window.Request) {
                        url = fetchInput.url;
                        if (fetchInput?.method) {
                            method = fetchInput.method;
                        }
                    } else {
                        url = '' + fetchInput;
                    }

                    // if Sentry key appears in URL, don't capture, as it's our own request
                    if (url.indexOf(globalKey) !== -1) {
                        return origFetch.apply(this, args);
                    }

                    if (args[1] && args[1].method) {
                        method = args[1].method;
                    }

                    const fetchData = {
                        method: method,
                        url: url,
                        status_code: null
                    };

                    return origFetch
                        .apply(this, args)
                        .then(function(response: any) {
                            fetchData.status_code = response.status;
                            let paramsBody = '';
                            if (args[1] && args[1].body) {
                                paramsBody = args[1].body;
                            }
                            // 是否能复制fetch的response，根据结果分两种处理
                            const responseBehavior = isFunction(response.clone) ? response.clone() : null;
                            const behaviorParams = {
                                type: 'http',
                                category: 'fetch',
                                data: {...fetchData, params: formatBehaviorData(paramsBody), response: ''} // method, url, status_code
                            }
                            if (responseBehavior) {
                                responseBehavior.text().then(text => {
                                    let res;
                                    try {
                                        res = JSON.parse(text);
                                    } catch(err) {
                                        res = text;
                                    }
                                    behaviorParams.data.response = formatBehaviorData(res);
                                    // 捕获 fetch 行为
                                    client.captureBehavior(behaviorParams);
                                });
                            } else {
                                // 捕获 fetch 行为
                                client.captureBehavior(behaviorParams);
                            }
                          
                            // 是否自动上报
                            if (!collectAutoAPIException) {
                                return response;
                            }
                            // 是否需要上报判断
                            let needCapture = false;

                            // 是否能复制fetch的response，根据结果分两种处理
                            const responseCopy = isFunction(response.clone) ? response.clone() : null;

                            // 根据状态码是否是重定向
                            const isRedirectAndCache = REDIRECT_CATCH_STATUS.includes(response.status);
                            // 请求失败且不为重定向
                            if (!response.ok && !isRedirectAndCache) {
                                needCapture = true;
                            }

                            const requestData = {
                                method: fetchData.method,
                                url: fetchData.url,
                                status: response.status,
                                response: ''
                            };
                            // 这里进行上报
                            const responseHeaders = [];
                            for (var pair of response.headers.entries()) {
                                responseHeaders.push(`${pair[0]}: ${pair[1]}`);
                            }
                            // 异常参数
                            const errorParams = {
                                ...requestData,
                                params: paramsBody,
                                responseHeaders: responseHeaders.join('***'),
                                user: '',
                                extra: 'AUTO_FETCH' // 标识是自动上报的 API 异常
                            }
                            if (responseCopy) {
                                // 都使用text()，只处理text和json格式
                                responseCopy.text().then(text => {
                                    let res;
                                    try {
                                        res = JSON.parse(text);
                                    } catch(err) {
                                        res = text;
                                    }

                                    if (response.ok && isFunction(isApiException)) {
                                        needCapture = !!isApiException({...requestData, response: res});
                                    }

                                    if (needCapture) {
                              
                                        client.captureAPIException({
                                            ...errorParams,
                                            response: text.slice(0, MAX_RES_LENGTH),
                                        });
                                    }
                                }).catch(e => {
                                    console.error(e);
                                });
                            } else {
                                // 无法复制fetch response时
                                // 自动上报 API 异常
                                if (response.ok && isFunction(isApiException)) {
                                    needCapture = !!isApiException(requestData);
                                }
                                if (needCapture) {
                                    // 这里进行上报
                                    client.captureAPIException(errorParams);
                                }
                            }

                            return response;
                        })
                        ['catch'](function(err: any) {
                            // if there is an error performing the request
                            client.captureBehavior({
                                type: 'http',
                                category: 'fetch',
                                data: fetchData,
                                level: 'error'
                            });

                            throw err;
                        });
                };
            },
            client._wrappedBuiltIns
        );
    }

    /**
     * 监听路由变更事件
     */
    instrumentHistory() {
        const client = this.client;

        if (!supportsHistory()) {
            return;
        }

        const captureUrlChange = (from: string | undefined, to: string | undefined): void => {
            const parsedLoc = parseUrl(global.location.href);
            const parsedTo = parseUrl(to as string);
            let parsedFrom = parseUrl(from as string);

            // Initial pushState doesn't provide `from` information
            if (!parsedFrom.path) {
                parsedFrom = parsedLoc;
            }

            // because onpopstate only tells you the "new" (to) value of location.href, and
            // not the previous (from) value, we need to track the value of the current URL
            // state ourselves
            lastHref = to;

            // Use only the path component of the URL if the URL matches the current
            // document (almost all the time when using pushState)
            if (parsedLoc.protocol === parsedTo.protocol && parsedLoc.host === parsedTo.host) {
                // tslint:disable-next-line:no-parameter-reassignment
                to = parsedTo.relative;
            }
            if (parsedLoc.protocol === parsedFrom.protocol && parsedLoc.host === parsedFrom.host) {
                // tslint:disable-next-line:no-parameter-reassignment
                from = parsedFrom.relative;
            }

            client.captureBehavior({
                category: 'navigation',
                data: {
                    from,
                    to
                }
            });
        };

        // record navigation (URL) changes
        const oldOnPopState = global.onpopstate;
        global.onpopstate = (args: any) => {
            const currentHref = global.location.href;
            captureUrlChange(lastHref, currentHref);
            if (oldOnPopState) {
                return oldOnPopState.apply(global, args);
            }
        };

        /** JSDoc */
        function historyReplacementFunction(originalHistoryFunction: () => void): () => void {
            // note history.pushState.length is 0; intentionally not declaring
            // params to preserve 0 arity
            return function(this: History, ...args: any): void {
                const url = args.length > 2 ? args[2] : undefined;
                // url argument is optional
                if (url) {
                    // coerce to string (this is what pushState does)
                    captureUrlChange(lastHref, String(url));
                }
                return originalHistoryFunction.apply(this, args);
            };
        }

        fill(global.history, 'pushState', historyReplacementFunction);
        fill(global.history, 'replaceState', historyReplacementFunction);
    }

    /**
     * 监听 Console 相关方法
     */
    instrumentConsole() {
        // TODO: TO BE ADDED
    }

    /**
     * 监听 Beacon 相关方法
     */
    instrumentBeacon() {
        // TODO: TO BE ADDED
    }
}
